import { useCallback, useState, useRef } from 'react';

import resumeCardGiftButtonClick from '@hh.ru/analytics-js-events/build/xhh/employer/responses/resume_card/resume_card_gift_button_click';
import { Tooltip, Tag, BottomSheet, Text, Button, BottomSheetFooter, NavigationBar } from '@hh.ru/magritte-ui';
import { GiftOutlinedSize16 } from '@hh.ru/magritte-ui/icon';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import Notices from 'Modules/Notices';
import { useVacancyIdNonNullable } from 'src/components/ResumeCard/hooks/useVacancyId';
import translation from 'src/components/translation';
import { useExperiment } from 'src/hooks/useExperiment';
import { useSelector } from 'src/hooks/useSelector';

import styles from './index.less';

const NOTICE_NAME = 'gifted_resume_label';

const TrlKeys = {
    tagText: 'suitable.resume.gift_label.tooltip.label',
    info: {
        text: 'suitable.resume.gift_label.tooltip.text',
        buy: 'suitable.resume.gift_label.tooltip.buy',
        close: 'suitable.resume.gift_label.tooltip.close',
    },
};

export interface ResumeGiftedType {
    isFirstGift: boolean;
}

const ResumeGifted: TranslatedComponent<ResumeGiftedType> = ({ isFirstGift, trls }) => {
    const isApplicantLivenessExp = useExperiment('liveness_tag', true, false);

    const vacancyId = useVacancyIdNonNullable();
    const showInfoTip = useSelector((state) => isFirstGift && state.infoTip.name === NOTICE_NAME);
    const [showTip, setShowTip] = useState(showInfoTip);
    const activatorRef = useRef<HTMLSpanElement>(null);

    const handleClick = useCallback(() => {
        resumeCardGiftButtonClick({ vacancyId: vacancyId.toString() });
        if (showInfoTip) {
            Notices.markAsViewed(NOTICE_NAME);
        }
        setShowTip((value) => !value);
    }, [showInfoTip, vacancyId]);

    const handleBuyClick = useCallback(() => {
        if (showInfoTip) {
            Notices.markAsViewed(NOTICE_NAME);
        }
        setShowTip(false);
        window.open('/price/dbaccess', '_blank', 'noopener');
    }, [showInfoTip]);

    const handleCloseClick = useCallback(() => {
        if (showInfoTip) {
            Notices.markAsViewed(NOTICE_NAME);
        }
        setShowTip(false);
    }, [showInfoTip]);

    return (
        <>
            <span
                data-qa="resume-gifted"
                onClick={handleClick}
                data-interactive
                className={styles.tagClickable}
                ref={activatorRef}
            >
                <Tag style={isApplicantLivenessExp ? 'accent' : 'special'} icon={GiftOutlinedSize16}>
                    {trls[TrlKeys.tagText]}
                </Tag>
            </span>
            <Tooltip
                placement="top-center"
                visible={showTip}
                onClose={handleCloseClick}
                closeByClickOutside={false}
                activatorRef={activatorRef}
                buttons={(onClose) => (
                    <>
                        <Button onClick={handleBuyClick} style="contrast" mode="primary" size="small">
                            {trls[TrlKeys.info.buy]}
                        </Button>
                        <Button style="contrast" mode="secondary" size="small" onClick={onClose}>
                            {trls[TrlKeys.info.close]}
                        </Button>
                    </>
                )}
            >
                {trls[TrlKeys.info.text]}
            </Tooltip>
            <BottomSheet
                visible={showTip}
                onClose={handleCloseClick}
                header={<NavigationBar title={trls[TrlKeys.tagText]} />}
                footer={
                    <BottomSheetFooter>
                        <Button mode="primary" style="accent" onClick={handleBuyClick}>
                            {trls[TrlKeys.info.buy]}
                        </Button>
                        <Button mode="secondary" style="accent" onClick={handleCloseClick}>
                            {trls[TrlKeys.info.close]}
                        </Button>
                    </BottomSheetFooter>
                }
            >
                <Text typography="label-2-regular">{trls[TrlKeys.info.text]}</Text>
            </BottomSheet>
        </>
    );
};

export default translation(ResumeGifted);
